import { describeMutationError } from "@api/mutationErrors";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useEnrollDevice } from "@capture/captureQueries";
import {
  Button,
  cn,
  Dialog,
  FieldError,
} from "@scribenote/brian-design-system";
import { formatCountdown } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import { useState } from "react";

const enrollErrorMessage = (error: unknown): string =>
  error instanceof ClinicAuthError && error.status === 503
    ? "Brian can’t reach the clinic computer. Check the connector is running on it, then try again."
    : describeMutationError(error, "get a code");

/** Rendered only while the room has no device, so the dialog closes itself once one joins. */
export const ConnectDeviceButton = ({
  roomUuid,
  roomName,
}: {
  roomUuid: string;
  roomName: string;
}) => {
  const [open, setOpen] = useState(false);
  const enroll = useEnrollDevice();
  const now = useNow(1000, open);
  const secondsLeft = enroll.data
    ? Math.max(0, Math.floor((enroll.data.expiresAt - now) / 1000))
    : 0;
  const expired = enroll.data !== undefined && secondsLeft === 0;

  const getCode = () => enroll.mutate(roomUuid);
  const close = () => {
    enroll.reset();
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true);
          getCode();
        }}
        aria-label={`Connect a device in ${roomName}`}
        data-testid={`room-connect-device-${roomUuid}`}
      >
        Connect device
      </Button>
      <Dialog
        open={open}
        onClose={close}
        size="sm"
        title={`Connect a device in ${roomName}`}
        footer={
          <>
            {(expired || enroll.isError) && (
              <Button
                variant="outline"
                onClick={getCode}
                busy={enroll.isPending}
              >
                Get a new code
              </Button>
            )}
            <Button onClick={close}>Close</Button>
          </>
        }
      >
        <div className="flex flex-col items-center gap-3 py-2">
          {enroll.isPending && (
            <span className="text-sm text-annotation">Getting a code…</span>
          )}
          {enroll.data && (
            <>
              <p
                data-testid="room-connect-device-instructions"
                className="text-sm text-ink-body"
              >
                On the tablet in {roomName}, open the Mike Cameron app and enter
                this code. Getting another code, for any room, cancels this one.
              </p>
              <div
                data-testid="room-connect-device-code"
                className={cn(
                  "font-mono text-5xl font-semibold tracking-[0.35em]",
                  expired ? "text-faint line-through" : "text-ink",
                )}
              >
                {enroll.data.code}
              </div>
              {expired ? (
                <span className="text-sm font-medium text-ink-stopped">
                  Code expired
                </span>
              ) : (
                <span
                  data-testid="room-connect-device-countdown"
                  className="text-sm text-annotation"
                  aria-live="polite"
                >
                  Expires in{" "}
                  <span className="font-mono font-medium text-ink tabular-nums">
                    {formatCountdown(secondsLeft)}
                  </span>
                </span>
              )}
            </>
          )}
          {enroll.isError && (
            <FieldError>{enrollErrorMessage(enroll.error)}</FieldError>
          )}
        </div>
      </Dialog>
    </>
  );
};
